
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Navigation from '../../components/feature/Navigation';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';

interface Job {
  id: string;
  clientName: string;
  documentType: string;
  destinationCountry: string;
  status: string;
  createdDate: string;
  totalAmount: number;
  paymentReceived: boolean;
}

export default function Jobs() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const savedJobs = JSON.parse(localStorage.getItem('apostille_jobs') || '[]');
    setJobs(savedJobs);
  }, []);

  const filteredJobs = jobs.filter(job => {
    if (filter === 'all') return true;
    if (filter === 'active') return job.status !== 'completed';
    if (filter === 'completed') return job.status === 'completed';
    if (filter === 'pending-payment') return !job.paymentReceived;
    return true;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'in-progress': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'in-progress': return 'In Progress';
      case 'completed': return 'Completed';
      default: return status;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header 
        title="Jobs" 
        rightAction={
          <Button
            size="sm"
            onClick={() => navigate('/jobs/new')}
            className="px-3 py-1.5"
          >
            <i className="ri-add-line mr-1"></i>
            New
          </Button>
        }
      />
      
      <div className="pt-16 pb-20 px-4">
        {/* Filter Tabs */}
        <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
          {[
            { key: 'all', label: 'All' },
            { key: 'active', label: 'Active' },
            { key: 'completed', label: 'Completed' },
            { key: 'pending-payment', label: 'Payment Due' }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`flex-1 px-3 py-2 text-xs font-medium rounded-md transition-colors ${
                filter === tab.key
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Jobs List */}
        <div className="space-y-4">
          {filteredJobs.length === 0 ? (
            <Card className="p-8">
              <div className="text-center text-gray-500">
                <i className="ri-file-list-line text-4xl mb-2"></i>
                <p className="text-sm">No jobs found</p>
                <p className="text-xs text-gray-400 mt-1">Create your first job to get started</p>
                <Button
                  className="mt-4"
                  onClick={() => navigate('/jobs/new')}
                >
                  Create New Job
                </Button>
              </div>
            </Card>
          ) : (
            filteredJobs.map((job) => (
              <Card 
                key={job.id} 
                className="p-4"
                onClick={() => navigate(`/jobs/${job.id}`)}
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{job.clientName}</h3>
                    <p className="text-sm text-gray-600">{job.documentType}</p>
                    <p className="text-xs text-gray-500">{job.destinationCountry}</p>
                  </div>
                  <div className="text-right">
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                      {getStatusText(job.status)}
                    </span>
                    {!job.paymentReceived && (
                      <div className="text-xs text-red-600 mt-1">Payment Due</div>
                    )}
                  </div>
                </div>
                <div className="flex justify-between items-center text-xs text-gray-500">
                  <span>Created: {new Date(job.createdDate).toLocaleDateString()}</span>
                  <span className="font-medium text-gray-900">${job.totalAmount}</span>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>

      <Navigation />
    </div>
  );
}
